import { useEffect, useRef, useState } from "react";
import type { GoogleCredentialResponse } from "@medplum/core";

import { isNullable } from "@/lib/utils";

type GoogleApi = {
  readonly accounts: {
    id: {
      initialize: (args: unknown) => void;
      renderButton: (parent: HTMLElement, args: unknown) => void;
    };
  };
};

declare global {
  /**
   * Window can only be extended through interface declaration merging.
   */
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    google?: GoogleApi;
  }
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

const loadGsiScript = (onLoad: () => void): void => {
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${GSI_SCRIPT_SRC}"]`
  );
  if (existing) {
    existing.addEventListener("load", onLoad, { once: true });
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = GSI_SCRIPT_SRC;
  script.onload = onLoad;
  document.head.appendChild(script);
};

type Props = {
  readonly googleClientId: string;
  readonly onCredential: (
    response: GoogleCredentialResponse
  ) => void | Promise<void>;
};

/**
 * Standalone GIS sign-in button
 *
 * SignInForm from @medplum/react prompts the user
 * to pick a membership/profile after login, which breaks Konko's UX: we
 * authenticate with the first membership immediately so the user lands in the
 * platform right away. Membership switching is available later in the layout.
 *
 * This ports only the GIS button pattern from @medplum/react, dropping the
 * Mantine dependency in favour of Tailwind.
 */
export const GoogleButton: React.FC<Props> = ({
  googleClientId,
  onCredential,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(!isNullable(window.google));
  const initializedRef = useRef(false);
  const buttonRenderedRef = useRef(false);

  useEffect(() => {
    if (isNullable(window.google)) {
      loadGsiScript(() => setScriptLoaded(true));
      return;
    }

    if (!initializedRef.current) {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: onCredential,
      });
      initializedRef.current = true;
    }

    if (parentRef.current && !buttonRenderedRef.current) {
      const containerWidth = parentRef.current.clientWidth;

      window.google.accounts.id.renderButton(parentRef.current, {
        type: "standard",
        size: "large",
        logo_alignment: "center",
        ...(containerWidth > 0 && { width: containerWidth }),
      });
      buttonRenderedRef.current = true;
    }
  }, [googleClientId, scriptLoaded, onCredential]);

  return <div ref={parentRef} className="h-10 w-full overflow-hidden" />;
};
